/**
 * Shared choreography for the scroll-driven car reveal. The car's own
 * rotation (`CarModel`) and the camera's dolly/height move (`CarStage`)
 * both key off these same numbers so they stay in lockstep, and so the
 * "never crop the car" math lives in exactly one place.
 *
 * Mental model: the camera sits at a fixed angle around the car (it never
 * orbits) and only dollies in/out along that one line of sight; the CAR
 * itself turns underneath it — front view at progress 0, through the full
 * side profile, to the rear at progress 1.
 *
 * Everything here is driven by the car's measured bounding box rather than
 * constants for one particular model, so swapping the `.glb` reframes the
 * shot automatically. See `carGeometry.js`.
 */

/** Angle (radians) from the car's front-facing axis where the camera sits. */
export const CAMERA_AZIMUTH = (37 * Math.PI) / 180;

/** Full 180°: front → side → rear. */
export const ROTATION_SWEEP = Math.PI;

/** The fixed, flattering three-quarter angle held when reduced motion is
 * preferred and the car does not turn. */
export const REDUCED_MOTION_PROGRESS = 0.4;

/** Pointer-parallax tilt limits, applied in `CarModel` and accounted for by
 * the framing solve so a lean toward the camera can't clip a corner. */
export const PARALLAX_TILT_X = 0.035;
export const PARALLAX_TILT_Z = 0.05;

export const CAMERA_FOV = 34;

/**
 * How much of the frame the car may occupy on its binding axis: the solve
 * targets `1 / CAMERA_MARGIN`, so 1.06 fills ~94% of the frame at the
 * tightest moment of the turn and holds back the last few percent as
 * breathing room.
 */
export const CAMERA_MARGIN = 1.06;

/** 0 = camera distance fixed at the worst case over the whole sweep at every
 * angle; 1 = distance fully tracks the car's apparent size. Kept high on
 * purpose so the car keeps a consistently commanding presence. */
export const CAMERA_DISTANCE_COMPENSATION = 0.94;

/**
 * Camera height across the reveal — a gentle crane-up — expressed as a
 * multiple of the car's own height above the point it is looking at.
 */
const CAMERA_RISE_NEAR = 0.98;
const CAMERA_RISE_FAR = 1.13;

const TAN_HALF_FOV = Math.tan((CAMERA_FOV / 2) * (Math.PI / 180));

/** Car yaw (radians) at a given scroll progress, 0–1. */
export function carYawAt(progress) {
  return CAMERA_AZIMUTH + progress * ROTATION_SWEEP;
}

/** The car is centred on the origin and stands on y=0, so its centre — and
 * the point the camera is aimed at — sits at half its height. */
function targetHeight(car) {
  return car.half.y;
}

function cameraHeightAt(progress, car) {
  const rise = CAMERA_RISE_NEAR + (CAMERA_RISE_FAR - CAMERA_RISE_NEAR) * progress;
  return targetHeight(car) + rise * car.half.y * 2;
}

/** The eight bounding-box corners of the normalised car. */
function cornersOf(car) {
  const out = [];
  for (const x of [-car.half.x, car.half.x])
    for (const y of [0, car.half.y * 2])
      for (const z of [-car.half.z, car.half.z]) out.push([x, y, z]);
  return out;
}

/** The four extreme parallax leans; the framing has to survive all of them. */
const TILTS = [
  [-PARALLAX_TILT_X, -PARALLAX_TILT_Z],
  [-PARALLAX_TILT_X, PARALLAX_TILT_Z],
  [PARALLAX_TILT_X, -PARALLAX_TILT_Z],
  [PARALLAX_TILT_X, PARALLAX_TILT_Z],
];

/**
 * Camera basis for the un-shifted rig at `radius`. The rig is later trucked
 * sideways/up as a whole (eye and target translated together), which leaves
 * the orientation — and therefore this basis — untouched.
 */
function basisAt(radius, cameraHeight, car) {
  const eyeX = radius * Math.sin(CAMERA_AZIMUTH);
  const eyeZ = radius * Math.cos(CAMERA_AZIMUTH);
  const rise = cameraHeight - targetHeight(car);
  const distance = Math.hypot(radius, rise);

  // forward runs from the eye down to the look-at target...
  const fx = -eyeX / distance;
  const fy = -rise / distance;
  const fz = -eyeZ / distance;
  // ...right = normalize(forward × worldUp), which stays level...
  const rightLen = Math.hypot(fz, fx);
  const rx = -fz / rightLen;
  const rz = fx / rightLen;
  // ...and up = right × forward, tipped forward as far as the camera is craned.
  return {
    eyeX,
    eyeY: cameraHeight,
    eyeZ,
    distance,
    fx,
    fy,
    fz,
    rx,
    rz,
    ux: -rz * fy,
    uy: rz * fx - rx * fz,
    uz: rx * fy,
  };
}

/**
 * Projects the car's bounding box, with the rig trucked by
 * (`offsetRight`, `offsetUp`), and reports both how much of the frame it
 * spans and how far off-centre it sits.
 *
 * The corner transform mirrors `CarModel` exactly: a Three.js Euler in its
 * default 'XYZ' order composes as Rx·Ry·Rz, i.e. Z is applied to the point
 * first and X last — not the order the name suggests.
 */
function silhouette(basis, corners, yaw, aspect, offsetRight, offsetUp) {
  const eyeX = basis.eyeX + basis.rx * offsetRight + basis.ux * offsetUp;
  const eyeY = basis.eyeY + basis.uy * offsetUp;
  const eyeZ = basis.eyeZ + basis.rz * offsetRight + basis.uz * offsetUp;

  const cosY = Math.cos(yaw);
  const sinY = Math.sin(yaw);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const [tiltX, tiltZ] of TILTS) {
    const cosX = Math.cos(tiltX);
    const sinX = Math.sin(tiltX);
    const cosZ = Math.cos(tiltZ);
    const sinZ = Math.sin(tiltZ);

    for (const [px, py, pz] of corners) {
      const zx = cosZ * px - sinZ * py;
      const zy = sinZ * px + cosZ * py;
      const wx = cosY * zx + sinY * pz;
      const rotZ = -sinY * zx + cosY * pz;
      const wy = cosX * zy - sinX * rotZ;
      const wz = sinX * zy + cosX * rotZ;

      const dx = wx - eyeX;
      const dy = wy - eyeY;
      const dz = wz - eyeZ;

      const depth = dx * basis.fx + dy * basis.fy + dz * basis.fz;
      if (depth <= 0.001) {
        return { fill: Number.POSITIVE_INFINITY, halfSpanY: 1, centerX: 0, centerY: 0 };
      }

      const ndcX = (dx * basis.rx + dz * basis.rz) / (depth * TAN_HALF_FOV * aspect);
      const ndcY = (dx * basis.ux + dy * basis.uy + dz * basis.uz) / (depth * TAN_HALF_FOV);

      if (ndcX < minX) minX = ndcX;
      if (ndcX > maxX) maxX = ndcX;
      if (ndcY < minY) minY = ndcY;
      if (ndcY > maxY) maxY = ndcY;
    }
  }

  return {
    fill: Math.max(maxX - minX, maxY - minY) / 2,
    halfSpanY: (maxY - minY) / 2,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
}

/**
 * Truck the rig until the car's silhouette is centred in frame, and report
 * the resulting fill. Shifting the eye and the target by the *same* vector
 * slides the image without altering the viewing angle, so the perspective
 * is untouched.
 */
function composeAt(radius, corners, yaw, cameraHeight, aspect, car, targetCenterY = 0) {
  const basis = basisAt(radius, cameraHeight, car);
  const scaleRight = basis.distance * TAN_HALF_FOV * aspect;
  const scaleUp = basis.distance * TAN_HALF_FOV;

  let offsetRight = 0;
  let offsetUp = 0;
  let shape = silhouette(basis, corners, yaw, aspect, offsetRight, offsetUp);

  for (let i = 0; i < 8; i += 1) {
    if (!Number.isFinite(shape.fill)) break;
    const errorY = shape.centerY - targetCenterY;
    if (Math.abs(shape.centerX) < 0.001 && Math.abs(errorY) < 0.001) break;
    offsetRight += shape.centerX * scaleRight;
    offsetUp += errorY * scaleUp;
    shape = silhouette(basis, corners, yaw, aspect, offsetRight, offsetUp);
  }

  return { basis, offsetRight, offsetUp, fill: shape.fill, halfSpanY: shape.halfSpanY };
}

/**
 * Smallest camera radius that keeps the whole car in frame with the
 * configured margin, once centred. Bisected rather than derived: apparent
 * size falls monotonically as the camera pulls back, and only the exact
 * projection accounts for a car framed from a few times its own length.
 */
function solveRadius(corners, yaw, cameraHeight, aspect, car) {
  const target = 1 / CAMERA_MARGIN;
  let lo = 0.5;
  let hi = 60;
  for (let i = 0; i < 22; i += 1) {
    const mid = (lo + hi) / 2;
    if (composeAt(mid, corners, yaw, cameraHeight, aspect, car).fill > target) lo = mid;
    else hi = mid;
  }
  return hi;
}

/**
 * Per-aspect table of exact fit radii across the sweep, plus the worst of
 * them. Solving is far too heavy to redo every frame, but it only depends
 * on the canvas shape and the model — so it is built once and sampled by
 * interpolation, which is smooth to well under a pixel between samples.
 */
const FIT_SAMPLES = 96;
const fitTables = new Map();

function fitTableFor(aspect, car) {
  const key = `${Math.round(aspect * 500)}:${Math.round(car.half.x * 1e4)}:${Math.round(car.half.y * 1e4)}:${Math.round(car.half.z * 1e4)}`;
  const cached = fitTables.get(key);
  if (cached) return cached;

  const corners = cornersOf(car);
  const radii = new Float64Array(FIT_SAMPLES + 1);
  let worst = 0;
  for (let i = 0; i <= FIT_SAMPLES; i += 1) {
    const progress = i / FIT_SAMPLES;
    const radius = solveRadius(corners, carYawAt(progress), cameraHeightAt(progress, car), aspect, car);
    radii[i] = radius;
    if (radius > worst) worst = radius;
  }

  const table = { radii, worst };
  fitTables.set(key, table);
  return table;
}

/**
 * Fraction of the frame's height kept clear above the car, so the hero's
 * copy has somewhere to sit.
 */
const TOP_HEADROOM = 0.17;

/** Never let the car settle right against the bottom edge. */
const EDGE_SAFETY = 0.01;

/**
 * How far to push the car down the frame, in NDC, to open up that headroom.
 * Only ever spends slack that is genuinely there.
 */
function headroomShift(halfSpanY) {
  const wanted = halfSpanY - (1 - 2 * TOP_HEADROOM);
  const available = 1 - halfSpanY - EDGE_SAFETY;
  return Math.max(0, Math.min(wanted, available));
}

/**
 * Where to put the camera for a given scroll progress, canvas aspect ratio,
 * and car.
 */
export function carFramingAt(progress, aspect, car) {
  const clamped = Math.min(Math.max(progress, 0), 1);
  const yaw = carYawAt(clamped);
  const cameraHeight = cameraHeightAt(clamped, car);

  const table = fitTableFor(aspect, car);
  const at = clamped * FIT_SAMPLES;
  const index = Math.min(Math.floor(at), FIT_SAMPLES - 1);
  const frac = at - index;
  const tracking = table.radii[index] * (1 - frac) + table.radii[index + 1] * frac;

  const fixed = Math.max(table.worst, tracking);
  const radius = fixed + CAMERA_DISTANCE_COMPENSATION * (tracking - fixed);

  const corners = cornersOf(car);

  // Centre first, measure how tall the car actually lands, then re-solve for
  // a frame biased down by whatever headroom that leaves room for.
  const centred = composeAt(radius, corners, yaw, cameraHeight, aspect, car);

  let shift = headroomShift(centred.halfSpanY);
  let composed = centred;
  for (let i = 0; i < 5 && shift > 0; i += 1) {
    const trial = composeAt(radius, corners, yaw, cameraHeight, aspect, car, -shift);
    const excess = shift + trial.halfSpanY - (1 - EDGE_SAFETY);
    if (excess <= 0) {
      composed = trial;
      break;
    }
    shift -= excess;
  }

  const { basis, offsetRight, offsetUp } = composed;

  return {
    position: [
      basis.eyeX + basis.rx * offsetRight + basis.ux * offsetUp,
      basis.eyeY + basis.uy * offsetUp,
      basis.eyeZ + basis.rz * offsetRight + basis.uz * offsetUp,
    ],
    target: [
      basis.rx * offsetRight + basis.ux * offsetUp,
      targetHeight(car) + basis.uy * offsetUp,
      basis.rz * offsetRight + basis.uz * offsetUp,
    ],
  };
}
